import { Comment } from '../domain/comment';
import { CommentType } from '../domain/enums/comment-type';
import { DateTime } from 'luxon';
import { AiDocumentType } from '../domain/enums/ai-document-type';
import { PaidOutTableRow } from '../domain/Ai/ModelData/paid-out-table-row';
import { CreditWindowTableRow } from '../domain/Ai/ModelData/credit-window-table-row';
import { ZeroRateTableRow } from '../domain/Ai/ModelData/zero-rate-table-row';
import { ModelSummary } from '../domain/Ai/model-summary';
import { RevenueAdjustmentTableRow } from '../domain/Ai/ModelData/revenue-adjustment-table-row';
import { PmReportsTableRow } from '../domain/Ai/ModelData/pm-reports-table-row';
import { OutOfOrderRoomsTableRow } from '../domain/Ai/ModelData/out-of-order-rooms-table-row';

export class AiDataTaskUtils {
   static GenerateAiCommentsByModel(aiData?: ModelSummary): Comment[] {
      const result: Comment[] = [];
      if (aiData) {
         const baseComment = new Comment();
         baseComment.type = CommentType.Ai;
         baseComment.createdUserFullName = 'Tyalls Automation';
         baseComment.createdDate = DateTime.now().toString();
         if (!aiData.isValid) {
            AiDataTaskUtils.CreateInvalidModelRun(aiData, baseComment, result);
         } else if (aiData.summaryData)
            switch (aiData.documentType) {
               case AiDocumentType.Other:
                  break;
               case AiDocumentType.PmAccounts:
                  /*
                  Disabled till after release as it's not working correctly
                  AiDataTaskUtils.CreateCommentTextForPmReport(
                     aiData,
                     baseComment,
                     result
                  );*/
                  break;
               case AiDocumentType.PaymentRefunds:
                  AiDataTaskUtils.CreateCommentTextForPaymentRefund(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.CreditLimit:
                  AiDataTaskUtils.CreateCommentTextForCreditLimit(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.ComplimentaryRooms:
                  AiDataTaskUtils.CreateCommentForZeroRate(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.OutOfOrderRooms:
                  AiDataTaskUtils.CreateCommentTextForOutOfOrderRooms(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.NoShowRooms:
                  AiDataTaskUtils.CreateCommentTextForNoShow(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.HousekeepingDiscrepancy:
                  break;
               case AiDocumentType.PaidOut:
                  AiDataTaskUtils.CreateCommentTextForPaidOut(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.TaComm:
                  AiDataTaskUtils.CreateCommentTextTaComm(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.RateChange:
                  AiDataTaskUtils.CreateCommentTextForRateChange(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
               case AiDocumentType.RevenueAdjustment:
                  AiDataTaskUtils.CreateCommentTextForRevenueAdjustment(
                     aiData,
                     baseComment,
                     result
                  );
                  break;
            }
      }
      return result;
   }
   static truncateString(string = '', maxLength = 50) {
      return string.length > maxLength
         ? `${string.substring(0, maxLength)}…`
         : string;
   }

   private static CreateInvalidModelRun(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText =
         'Alert: unable to process report. Please check the report date, report name and filters to validate report.<br />';
      baseComment.text = `${commentText}`;
      commentArray.push(baseComment);
   }

   private static formatCurrencyForDisplay(
      value: number,
      swapNegative: boolean = false
   ): string {
      return `$${AiDataTaskUtils.formatNumberForDisplay(value, swapNegative)}`;
   }

   private static formatNumberForDisplay(
      value: number,
      swapNegative: boolean = false
   ): string {
      if (swapNegative) {
         return (value * -1).toFixed(2);
      }
      return value.toFixed(2);
   }

   private static CreateCommentTextForCreditLimit(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText =
         "For Action: The listed Room No.'s may be at risk of default:<br />";
      let commentAiText = '';
      ///This is messy and I hate it.. I had the classes all setup to do this themselves but JS hates types
      for (let i = 0; i < aiData.summaryData.length; i++) {
         const summaryData = aiData.summaryData[i] as CreditWindowTableRow;
         let balanceSum = 0;
         if (summaryData.creditLimitWindowTableRows) {
            for (let j = 0; j < summaryData.creditLimitWindowTableRows.length; j++) {
               const creditWindowTableRow = summaryData.creditLimitWindowTableRows[
                  j
               ] as CreditWindowTableRow;
               const creditLimit = creditWindowTableRow.creditLimit;
               const balance = creditWindowTableRow.balance;
               if (creditLimit && balance && creditLimit - balance < 0) {
                  balanceSum += creditWindowTableRow.balance
                     ? creditWindowTableRow.balance
                     : 0;
               }
            }
         }
         commentAiText += `${summaryData.roomNumber} ${AiDataTaskUtils.formatNumberForDisplay(
            balanceSum,
            false
         )} <br />`;
      }
      if (commentAiText !== '') {
         baseComment.text = `${commentText} ${commentAiText}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForPaidOut(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText =
         'Please attach Support File of approval of each Paid Out:<br />';
      let paidOutSum = 0;
      for (let i = 0; i < aiData.summaryData.length; i++) {
         const paidOutRow = aiData.summaryData[i] as PaidOutTableRow;
         if (paidOutRow.roomNumber) {
            paidOutSum += paidOutRow.debit ? paidOutRow.debit : 0;
         }
      }
      if (paidOutSum > 0) {
         baseComment.text = `${commentText} ${AiDataTaskUtils.formatCurrencyForDisplay(paidOutSum, false)}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForNoShow(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText = 'Action may be required for the No Show rooms:<br />';
      if (aiData.summaryData.length > 0) {
         baseComment.text = `${commentText} ${aiData.summaryData.length}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentForZeroRate(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const marketCompComment = structuredClone(baseComment);
      const marketCompZeroRateComment =
         "For Action: Supporting Files of Comp. Room Approval may be required for the following Room No.'s:<br />";
      const standardZeroRateComment =
         'For Action: Please upload evidence of approval of each Complimentary Room to Supporting Files<br />';
      const roomCount = aiData.summaryData.reduce((accumulator, currentValue) => {
         if (currentValue.prs) {
            accumulator += 1;
         }
         return accumulator;
      }, 0);
      let marketCompAiText = '';
      for (let i = 0; i < aiData.summaryData.length; i++) {
         const zeroRateRow = aiData.summaryData[i] as ZeroRateTableRow;
         if (zeroRateRow.marketCode === 'COMP') {
            marketCompAiText += `${zeroRateRow.roomNumber} <br />`;
         }
      }
      if (marketCompAiText !== '') {
         marketCompComment.text = `${marketCompZeroRateComment} ${marketCompAiText}`;
         commentArray.push(marketCompComment);
      }
      if (roomCount > 0) {
         baseComment.text = `${standardZeroRateComment} ${roomCount}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextTaComm(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText =
         'For Action: Verify correct commission rates by adding Supporting Files.';
      if (aiData.summaryData.length > 0) {
         baseComment.text = `${commentText}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForRateChange(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText =
         'Action required: please review the changes to Rates and add Supporting Files where evidence is required.';
      if (aiData.summaryData.length > 0) {
         baseComment.text = `${commentText}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForPaymentRefund(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentText =
         ' Action required: please review the Payment Refunds and Supporting Files';
      if (aiData.summaryData.length > 0) {
         baseComment.text = `${commentText}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForPmReport(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const commentRegex = /[,.-]/g;
      const pmReportCommentText =
         'For Action: The listed PM Accounts balances may be at risk of default:<br />';
      let pmReportsAiText = '';
      for (let i = 0; i < aiData.summaryData.length; i++) {
         const pmReportRow = aiData.summaryData[i] as PmReportsTableRow;
         if (pmReportRow.balance) {
            pmReportsAiText += `${pmReportRow.roomNumber ? pmReportRow.roomNumber : ''} - ${AiDataTaskUtils.truncateString(pmReportRow.name?.replace(commentRegex, ''), 10)}: ${AiDataTaskUtils.formatCurrencyForDisplay(pmReportRow.balance, false)} <br />`;
         }
      }
      if (pmReportsAiText) {
         baseComment.text = `${pmReportCommentText} ${pmReportsAiText}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForRevenueAdjustment(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const revenueAdjustmentCommentText =
         'For Action: Please review the following Revenue Adjustments:<br />';
      let revenueAdjustmentAiText = '';
      for (let i = 0; i < aiData.summaryData.length; i++) {
         const revenueAdjustmentRow = aiData.summaryData[
            i
         ] as RevenueAdjustmentTableRow;
         if (revenueAdjustmentRow.debit) {
            revenueAdjustmentAiText += `${revenueAdjustmentRow.roomNumber} - ${AiDataTaskUtils.formatCurrencyForDisplay(revenueAdjustmentRow.debit, true)} <br />`;
         }
      }

      if (revenueAdjustmentAiText !== '') {
         baseComment.text = `${revenueAdjustmentCommentText} ${revenueAdjustmentAiText}`;
         commentArray.push(baseComment);
      }
   }

   private static CreateCommentTextForOutOfOrderRooms(
      aiData: ModelSummary,
      baseComment: Comment,
      commentArray: Comment[]
   ) {
      const outOfOrderRoomsCommentText =
         'For Action: Please review the following Revenue Adjustments:<br />';
      let outOfOrderRoomsAiText = '';
      for (let i = 0; i < aiData.summaryData.length; i++) {
         const outOfOrderRoomsRow = aiData.summaryData[i] as OutOfOrderRoomsTableRow;
         if (aiData.taskDate && outOfOrderRoomsRow.start) {
            const reportDate = DateTime.fromISO(aiData.taskDate);
            const startDate = DateTime.fromISO(outOfOrderRoomsRow.start);
            if (reportDate > startDate || !outOfOrderRoomsRow.remarks) {
               outOfOrderRoomsAiText += `${outOfOrderRoomsRow.roomNumber} - ${outOfOrderRoomsRow.start} <br />`;
            }
         }
      }
      if (outOfOrderRoomsAiText) {
         baseComment.text = `${outOfOrderRoomsCommentText} ${outOfOrderRoomsAiText}`;
         commentArray.push(baseComment);
      }
   }
}
